import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, X } from 'lucide-react';
// Track analytics events (similar to ServiceCategories)
const trackEvent = (eventName: string, eventData: any) => {
  console.log(`Analytics Event: ${eventName}`, eventData);
  // In a real app, this would call your analytics service
  const events = JSON.parse(sessionStorage.getItem('analyticsEvents') || '[]');
  events.push({
    eventName,
    eventData,
    timestamp: new Date().toISOString()
  });
  sessionStorage.setItem('analyticsEvents', JSON.stringify(events));
};
export const NewsUpdates = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedNews, setSelectedNews] = useState<any>(null);
  const handleReadMore = (e: React.MouseEvent, item: any) => {
    e.preventDefault();
    setSelectedNews(item);
    setShowModal(true);
    // Track the event
    trackEvent('news_article_opened', {
      articleId: item.id,
      articleTitle: item.title,
      timestamp: new Date().toISOString()
    });
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedNews(null);
  };
  const news = [{
    id: 1,
    title: 'New Online Tax Filing System Launched',
    date: 'June 15, 2023',
    excerpt: 'The government has launched a new online tax filing system to simplify the process for citizens and businesses.',
    link: '/news/tax-filing-system',
    fullContent: `
        <p>The Ministry of Finance today announced the launch of a new online tax filing system designed to streamline the tax submission process for both individuals and businesses.</p>
        <p>The new system, called "TaxEase," allows users to complete their entire tax filing process online, including document uploads, payment processing, and receipt generation. The platform is expected to reduce processing time by up to 70% compared to the previous paper-based system.</p>
        <p>"This represents a major step forward in our digital transformation journey," said Finance Minister Sarah Johnson. "Citizens can now file their taxes from the comfort of their homes, without the need to visit government offices or stand in long queues."</p>
        <p>Key features of the new system include:</p>
        <ul>
          <li>Pre-filled forms using existing government data</li>
          <li>Real-time calculation and verification</li>
          <li>Secure document upload capabilities</li>
          <li>Multiple payment options including credit cards and mobile money</li>
          <li>Instant digital receipts</li>
        </ul>
        <p>The system will be rolled out in phases, with individual taxpayers gaining access this month, followed by small businesses in August, and larger corporations in September.</p>
        <p>Training sessions and help centers will be established across the country to assist citizens who may need guidance with the new system.</p>
      `
  }, {
    id: 2,
    title: 'E-Visa Applications Now Available for All Countries',
    date: 'May 28, 2023',
    excerpt: 'Visitors from all countries can now apply for e-visas online, making travel to the country easier than ever.',
    link: '/news/e-visa-expansion',
    fullContent: `
        <p>The Department of Immigration has expanded its e-visa program to include visitors from all countries, effective immediately. This expansion marks the completion of a three-year digital transformation project for the immigration system.</p>
        <p>Previously, the e-visa system was only available to citizens of 45 countries. With this expansion, travelers from anywhere in the world can now apply for tourist, business, or medical visas through the online portal.</p>
        <p>"We're proud to make our country more accessible to visitors from around the globe," said Immigration Director Thomas Chen. "This system not only simplifies the application process but also enhances security through advanced verification technologies."</p>
        <p>The new system reduces visa processing time from an average of 7 days to just 48 hours in most cases. Applicants receive updates via email and can track their application status through an online dashboard.</p>
        <p>Tourism industry leaders have welcomed the move, predicting a 15% increase in international visitors over the next year as a result of the simplified entry process.</p>
        <p>The e-visa portal includes support for 12 languages and offers 24/7 customer service through a chatbot and email support system.</p>
      `
  }, {
    id: 3,
    title: 'Digital ID System Upgrade Scheduled for Next Month',
    date: 'May 10, 2023',
    excerpt: 'The national digital ID system will undergo upgrades to improve security and add new features.',
    link: '/news/digital-id-upgrade',
    fullContent: `
        <p>The National Identity Authority has announced a major upgrade to the digital ID system, scheduled to take place from June 5-8, 2023. During this period, some digital ID services may experience intermittent availability.</p>
        <p>The upgrade will introduce enhanced security features, including advanced biometric verification and blockchain-based data protection. Additionally, citizens will gain access to new functionalities such as digital signatures and secure document storage.</p>
        <p>"These improvements represent our commitment to providing citizens with state-of-the-art digital identity services while ensuring the highest standards of data protection," said Dr. Lisa Wong, Director of Digital Identity Services.</p>
        <p>Key enhancements include:</p>
        <ul>
          <li>Improved facial recognition algorithms with liveness detection</li>
          <li>Integration with more government services</li>
          <li>Enhanced privacy controls allowing citizens to monitor access to their data</li>
          <li>Support for digital signatures on legal documents</li>
          <li>A redesigned mobile app with improved user experience</li>
        </ul>
        <p>Citizens are advised to complete any urgent digital ID-related transactions before June 5. A helpline (0800-123-4567) will be available 24/7 during the upgrade period to assist with any emergencies.</p>
        <p>Following the upgrade, users will need to update their mobile applications and complete a one-time verification process to access the new features.</p>
      `
  }];
  return <section className="py-16 bg-white" data-id="element-57">
      <div className="container mx-auto px-4" data-id="element-58">
        <div className="flex justify-between items-center mb-10" data-id="element-59">
          <div data-id="element-60">
            <h2 className="text-3xl font-bold mb-2" data-id="element-61">News & Updates</h2>
            <p className="text-gray-600" data-id="element-62">
              Latest announcements and service updates
            </p>
          </div>
          <Link to="/news" className="flex items-center text-blue-600 font-medium hover:text-blue-800" data-id="element-63">
            View all news
            <ArrowRight className="ml-2 h-4 w-4" data-id="element-64" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-65">
          {news.map(item => <div key={item.id} className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-66">
              <div className="p-6" data-id="element-67">
                <div className="flex items-center text-gray-500 text-sm mb-3" data-id="element-68">
                  <Calendar className="h-4 w-4 mr-2" data-id="element-69" />
                  {item.date}
                </div>
                <h3 className="text-xl font-semibold mb-3" data-id="element-70">
                  <Link to={item.link} className="hover:text-blue-600" data-id="element-71">
                    {item.title}
                  </Link>
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-72">{item.excerpt}</p>
                <a href="#" onClick={e => handleReadMore(e, item)} className="text-blue-600 font-medium hover:text-blue-800 flex items-center" data-id="element-73">
                  Read more
                  <ArrowRight className="ml-2 h-4 w-4" data-id="element-74" />
                </a>
              </div>
            </div>)}
        </div>
      </div>
      {/* News Article Modal */}
      {showModal && selectedNews && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-75">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-id="element-76">
            <div className="p-6" data-id="element-77">
              <div className="flex justify-between items-center mb-6" data-id="element-78">
                <h2 className="text-2xl font-bold" data-id="element-79">{selectedNews.title}</h2>
                <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700" data-id="element-80">
                  <X className="h-6 w-6" data-id="element-81" />
                </button>
              </div>
              <div className="flex items-center text-gray-500 text-sm mb-6" data-id="element-82">
                <Calendar className="h-4 w-4 mr-2" data-id="element-83" />
                {selectedNews.date}
              </div>
              <div className="prose max-w-none" dangerouslySetInnerHTML={{
            __html: selectedNews.fullContent
          }} data-id="element-84" />
              <div className="mt-6 flex justify-end" data-id="element-85">
                <button onClick={handleCloseModal} className="px-8 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-md transition-colors" data-id="element-86">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}
    </section>;
};